import React, { useState, useEffect } from 'react';

interface ForSaleImageIdentifyProps {
  onIdentified: (result: string, images: string[]) => void;
  onConfirmWithPrice?: (result: string, images: string[]) => void;
  aiAnalysis?: boolean;
}

export function ForSaleImageIdentify({ onIdentified, onConfirmWithPrice, aiAnalysis = false }: ForSaleImageIdentifyProps) {
  const uploadInputRef = React.useRef<HTMLInputElement>(null);
  const cameraInputRef = React.useRef<HTMLInputElement>(null);
  const [images, setImages] = useState<string[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [aiResult, setAiResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [correction, setCorrection] = useState('');

  function handleFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setError(null);
    // Limit number of files
    if (files.length > 4) {
      setError('You can upload a maximum of 4 photos.');
      return;
    }
    // Limit file size
    const tooLarge = files.find((file) => file.size > 3 * 1024 * 1024);
    if (tooLarge) {
      setError('Each photo must be 3 MB or less.');
      return;
    }
    const readers = files.map(file => {
      return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    });
    Promise.all(readers)
      .then(imgs => {
        setImages(imgs);
        setPreviewUrls(imgs);
        // Always start AI analysis automatically if aiAnalysis is enabled
        if (aiAnalysis && imgs.length > 0) {
          handleIdentifyAuto(imgs); // This automatically calls onIdentified when done
        } else if (imgs.length > 0) {
          // Without AI analysis, just notify parent of uploaded images
          onIdentified('', imgs);
        }
      })
      .catch(() => setError('Failed to load images.'));
  }

  // handleIdentify functionality is now integrated into handleFiles when aiAnalysis is true

  async function handleIdentifyAuto(imgs: string[]) {
    setIsUploading(true);
    setError(null);
    setAiResult(null);
    setConfirmed(false);
    try {
      const res = await fetch('/api/identify-item', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ images: imgs }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to identify item');
      setAiResult(data.answer);
      // Set confirmed to true to indicate we've processed the AI result
      setConfirmed(true);
      // Always pass a non-empty string to ensure the button is enabled
      onIdentified(data.answer || 'Unidentified item', imgs);
      
      // Directly trigger price research if callback provided
      const result = data.answer || 'Unidentified item';
      if (onConfirmWithPrice && result.trim()) {
        onConfirmWithPrice(result, imgs);
      }
    } catch (err: any) {
      setError(err.message || 'Unknown error');
      // Even on error, pass a default value to ensure the button is enabled
      onIdentified('Unidentified item', imgs);
    } finally {
      setIsUploading(false);
    }
  }

  function handleConfirm() {
    setConfirmed(true);
    // Always pass a non-empty string to ensure the button is enabled
    const result = aiResult || correction || 'Unidentified item';
    onIdentified(result, images);
    
    // Directly trigger price research if callback provided
    if (onConfirmWithPrice && result.trim()) {
      onConfirmWithPrice(result, images);
    }
  }

  // Watch for changes in aiAnalysis and trigger analysis if we have images
  useEffect(() => {
    if (aiAnalysis && images.length > 0) {
      // When AI Analysis is turned on and we have images, analyze them
      handleIdentifyAuto(images);
    }
  }, [aiAnalysis]); // Only run when aiAnalysis changes

  function handleCorrectionChange(e: React.ChangeEvent<HTMLInputElement>) {
    setCorrection(e.target.value);
  }

  function isMobileDevice() {
    if (typeof navigator === 'undefined') return false;
    return /Mobi|Android|iPhone|iPad|iPod|Opera Mini|IEMobile|Mobile/i.test(navigator.userAgent);
  }

  return (
    <div className="space-y-4 w-full max-w-full overflow-x-hidden">
      <div className="flex flex-row gap-2 w-full mb-2">
  {/* Upload Photo Button */}
  <button
    type="button"
    className="w-1/2 min-h-[44px] px-4 py-2 bg-blue-600 text-white rounded-md font-semibold shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
    aria-label="Upload photo(s) from device"
    onClick={() => {
      if (uploadInputRef.current) uploadInputRef.current.click();
    }}
    disabled={isUploading}
    style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
  >
    Upload Photo
  </button>
  <input
    ref={uploadInputRef}
    type="file"
    accept="image/*"
    multiple
    className="hidden"
    aria-label="Upload photo(s) from device"
    onChange={handleFiles}
    disabled={isUploading}
  />
  {/* Camera Button - only on mobile */}
  {isMobileDevice() && (
    <>
      <button
        type="button"
        className="w-1/2 min-h-[44px] px-4 py-2 bg-blue-600 text-white rounded-md font-semibold shadow flex items-center gap-2 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
        aria-label="Take a photo with device camera"
        onClick={() => {
          if (cameraInputRef.current) cameraInputRef.current.click();
        }}
        disabled={isUploading}
        style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5"><path d="M4 5a2 2 0 012-2h1.172a2 2 0 011.414.586l.828.828A2 2 0 0110.828 5H14a2 2 0 012 2v8a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm6 4a3 3 0 100 6 3 3 0 000-6zm0 2a1 1 0 110 2 1 1 0 010-2z" /></svg>
        Camera
      </button>
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        aria-label="Take a photo with device camera"
        onChange={e => {
          // Only allow one photo for camera capture
          if (e.target.files && e.target.files.length > 0) {
            const evt = { ...e, target: { ...e.target, files: e.target.files } };
            handleFiles(evt as React.ChangeEvent<HTMLInputElement>);
          }
        }}
        disabled={isUploading}
      />
    </>
  )}
</div>
      {error && (
        <div className="text-red-600 text-sm font-medium mt-2">{error}</div>
      )}
      {previewUrls.length > 0 && (
        <div className="flex gap-2 flex-wrap w-full overflow-x-auto">
          {previewUrls.map((url, i) => (
            <img key={i} src={url} alt={`preview-${i}`} className="w-24 h-24 object-cover rounded border max-w-full" />
          ))}
        </div>
      )}
      {/* AI Analysis happens automatically after upload when aiAnalysis is true */}
      {isUploading && (
        <div className="px-4 py-2 text-center text-blue-800 font-semibold">
          Identifying item with AI...
        </div>
      )}
      {error && <div className="text-red-600 bg-red-50 border border-red-200 rounded p-2 text-sm">{error}</div>}
      {aiResult && !confirmed && (
        <div className="space-y-2 bg-white shadow-lg border border-gray-200 rounded mb-8 mt-6 w-full px-2 sm:max-w-xl sm:mx-auto sm:px-8 sm:py-6 px-4 py-4 z-10 relative">
          <div className="font-bold text-gray-800">AI thinks this is:</div>
          {aiResult.split(/\.\s+/).map((p, i) => (
            <div key={i} className="text-blue-800 font-semibold whitespace-pre-line break-words mb-2">{p}.</div>
          ))}
          <div className="flex flex-col gap-2 sm:flex-row items-stretch mt-2">
            <button
              className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 font-semibold"
              onClick={handleConfirm}
            >
              Confirm
            </button>
            <span className="text-gray-500">or</span>
            <input
              type="text"
              className="border border-gray-300 rounded px-2 py-1 flex-grow min-w-0"
              placeholder="Correct description"
              value={correction}
              onChange={handleCorrectionChange}
            />
            <button
              className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 font-semibold"
              onClick={handleConfirm}
              disabled={!correction.trim()}
            >
              Submit Correction
            </button>
          </div>
        </div>
      )}
      {confirmed && (
        <div className="text-green-700 font-medium">Item identification confirmed.</div>
      )}
    </div>
  );
}
